import { useState, useEffect, useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import { approvalsApi } from "../lib/api";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { CheckSquare, X, Check } from "lucide-react";
import { UserAvatar } from "../components/UserAvatar";

const Approvals = () => {
  const { isAdmin } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [processing, setProcessing] = useState(false);

  useEffect(() => { loadPendingEntries(); }, []);

  const loadPendingEntries = async () => {
    try {
      const response = await approvalsApi.getPending();
      setEntries(response.data);
    } catch {
      toast.error("Kunde inte ladda poster");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (entryId) => {
    setProcessing(true);
    try {
      await approvalsApi.process(entryId, "approve");
      setEntries(prev => prev.filter(e => e.id !== entryId));
      setSelectedIds(prev => prev.filter(id => id !== entryId));
      toast.success("Godkänd");
    } catch {
      toast.error("Kunde inte godkänna");
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async (entryId) => {
    setProcessing(true);
    try {
      await approvalsApi.process(entryId, "reject");
      setEntries(prev => prev.filter(e => e.id !== entryId));
      setSelectedIds(prev => prev.filter(id => id !== entryId));
      toast.success("Avvisad");
    } catch {
      toast.error("Kunde inte avvisa");
    } finally {
      setProcessing(false);
    }
  };

  const handleBulkApprove = async () => {
    if (selectedIds.length === 0) return;
    setProcessing(true);
    try {
      await approvalsApi.bulkProcess(selectedIds, "approve");
      setEntries(prev => prev.filter(e => !selectedIds.includes(e.id)));
      toast.success(`${selectedIds.length} poster godkända`);
      setSelectedIds([]);
    } catch {
      toast.error("Kunde inte godkänna");
    } finally {
      setProcessing(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const selectUserEntries = (userEntries) => {
    const ids = userEntries.map(e => e.id);
    const allSelected = ids.every(id => selectedIds.includes(id));
    if (allSelected) {
      setSelectedIds(prev => prev.filter(id => !ids.includes(id)));
    } else {
      setSelectedIds(prev => [...new Set([...prev, ...ids])]);
    }
  };

  const entriesByUser = useMemo(() => entries.reduce((acc, entry) => {
    const userName = entry.user_name || "Okand";
    if (!acc[userName]) acc[userName] = [];
    acc[userName].push(entry);
    return acc;
  }, {}), [entries]);

  const totalHours = useMemo(() => entries.reduce((sum, e) => sum + e.hours, 0), [entries]);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#6dbf4a]"></div></div>;
  }

  return (
    <div className="space-y-5 slide-up" data-testid="approvals-page">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <CheckSquare className="w-5 h-5 text-[#6dbf4a]" />
          <h1 className="text-2xl font-bold text-white tracking-tight">Godkännanden</h1>
        </div>
        <p className="text-white/25 text-sm">
          {entries.length > 0
            ? `${entries.length} poster · ${totalHours.toFixed(1)}h att granska`
            : "Inga poster att granska"}
        </p>
      </div>

      {/* Bulk actions - sticky */}
      {entries.length > 0 && (
        <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-[#0d1218] border border-white/[0.06]">
          <Checkbox
            checked={selectedIds.length === entries.length && entries.length > 0}
            onCheckedChange={() => setSelectedIds(selectedIds.length === entries.length ? [] : entries.map(e => e.id))}
            data-testid="select-all-btn" />
          <span className="text-sm text-white/40 flex-1">
            {selectedIds.length > 0 ? `${selectedIds.length} valda` : "Välj alla"}
          </span>
          {selectedIds.length > 0 && (
            <Button size="sm" onClick={handleBulkApprove} disabled={processing}
              className="bg-[#6dbf4a] hover:bg-[#5da83d] h-9 gap-1.5" data-testid="bulk-approve-btn">
              <Check className="w-4 h-4" /> Godkänn {selectedIds.length} poster
            </Button>
          )}
        </div>
      )}

      {/* Entries grouped by user */}
      {Object.keys(entriesByUser).length > 0 ? (
        <div className="space-y-3">
          {Object.entries(entriesByUser).map(([userName, userEntries]) => {
            const userHours = userEntries.reduce((s, e) => s + e.hours, 0);
            const allSelected = userEntries.every(e => selectedIds.includes(e.id));

            return (
              <div key={userName} data-testid={`user-entries-${userName}`}>
                {/* User header */}
                <div className="flex items-center gap-3 px-3 py-2 mb-1">
                  <Checkbox checked={allSelected} onCheckedChange={() => selectUserEntries(userEntries)} />
                  <UserAvatar userId={userEntries[0]?.user_id} name={userName} className="w-8 h-8" testId={`approval-avatar-${userEntries[0]?.user_id}`} />
                  <span className="text-white font-medium text-sm">{userName}</span>
                  <span className="text-white/30 text-xs">{userEntries.length} poster · {userHours.toFixed(1)}h</span>
                </div>

                {/* Entries */}
                <div className="space-y-1">
                  {userEntries.map(entry => (
                    <div key={entry.id}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-[#0d1218] hover:bg-[#111820] transition-colors"
                      data-testid={`approval-entry-${entry.id}`}>
                      <Checkbox checked={selectedIds.includes(entry.id)}
                        onCheckedChange={() => toggleSelect(entry.id)}
                        data-testid={`checkbox-${entry.id}`} />

                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white truncate">
                          <span className="font-medium">{entry.project_name}</span>
                          <span className="text-white/30 mx-1">·</span>
                          <span className="text-white/40">{entry.activity_name}</span>
                        </p>
                        <p className="text-[11px] text-white/25 mt-0.5">
                          {format(new Date(entry.date), "EEE d MMM", { locale: sv })}
                          {entry.description && <span className="ml-1 italic">— {entry.description}</span>}
                        </p>
                      </div>

                      <span className="text-white font-bold text-base flex-shrink-0">{entry.hours}h</span>

                      <div className="flex gap-1 flex-shrink-0">
                        <button onClick={() => handleApprove(entry.id)} disabled={processing}
                          className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#6dbf4a]/15 text-[#6dbf4a] hover:bg-[#6dbf4a]/25 transition-colors"
                          data-testid={`approve-${entry.id}`}>
                          <Check className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleReject(entry.id)} disabled={processing}
                          className="w-9 h-9 flex items-center justify-center rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"
                          data-testid={`reject-${entry.id}`}>
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-20">
          <div className="w-14 h-14 rounded-xl bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
            <CheckSquare className="w-7 h-7 text-emerald-500/50" />
          </div>
          <p className="text-white/40 text-sm">Inga poster väntar på godkännande</p>
        </div>
      )}
    </div>
  );
};

export default Approvals;
